import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ROLE_SEEDS, createUserSchema, type CreateUserInput } from '@cumpli/shared';
import { api, RequestError } from '../lib/api';
import { useRoles } from '../lib/hooks';
import { useToast } from '../lib/use-toast';
import { Button, Card, FieldError, Input, Label, PageHeader } from '../components/ui';

// The API role catalog carries only key + name; the es-PA capability copy lives
// on the shared ROLE_SEEDS. Look it up by key so each option shows what it grants.
const roleDescription = (key: string) => ROLE_SEEDS.find((r) => r.key === key)?.description;

export default function UserCreate() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { data: roles } = useRoles();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<CreateUserInput>({
    resolver: zodResolver(createUserSchema),
    defaultValues: { roleKeys: [] },
  });

  const create = useMutation({
    mutationFn: api.createUser,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      toast({ message: t('users.created'), kind: 'success' });
      navigate('/usuarios');
    },
    onError: (err) => {
      if (err instanceof RequestError && err.status === 409) {
        setError('email', { message: t('users.duplicateEmail') });
      } else {
        toast({ message: t('common.genericError'), kind: 'error' });
      }
    },
  });

  return (
    <div>
      <PageHeader title={t('users.newTitle')} subtitle="Crear acceso para un nuevo usuario del tenant" />
      <Card className="max-w-lg">
        <form onSubmit={handleSubmit((data) => create.mutate(data))} className="space-y-4">
          <div>
            <Label htmlFor="fullName">{t('users.fullName')} *</Label>
            <Input id="fullName" {...register('fullName')} />
            <FieldError message={errors.fullName && t('users.nameLength')} />
          </div>
          <div>
            <Label htmlFor="email">{t('users.email')} *</Label>
            <Input id="email" type="email" {...register('email')} />
            <FieldError
              message={errors.email && (errors.email.message ?? t('users.invalidEmail'))}
            />
          </div>
          <fieldset>
            <Label>{t('users.roles')} *</Label>
            <div className="space-y-2">
              {roles?.map((r) => {
                const description = roleDescription(r.key);
                return (
                  <label
                    key={r.key}
                    className="flex gap-2.5 rounded-xl border border-line bg-panel p-3 text-sm transition-colors hover:border-ink-faint"
                  >
                    <input
                      type="checkbox"
                      value={r.key}
                      {...register('roleKeys')}
                      className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
                    />
                    <span>
                      <span className="font-medium text-ink">{r.name}</span>
                      {description && (
                        <span className="mt-0.5 block text-xs leading-snug text-ink-soft">
                          {description}
                        </span>
                      )}
                    </span>
                  </label>
                );
              })}
            </div>
            <FieldError message={errors.roleKeys && t('users.rolesRequired')} />
          </fieldset>
          <div className="flex gap-3">
            <Button type="submit" disabled={isSubmitting || create.isPending}>
              {t('common.save')}
            </Button>
            <Button type="button" variant="outline" onClick={() => navigate('/usuarios')}>
              {t('common.cancel')}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
